import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';

import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import {
    Button,
    ButtonColor,
    ButtonShape,
    ButtonSize,
    ButtonType,
    ButtonVariant,
    RenderType,
} from '@jsmdg/yoshi';
import { FooterLinkTrackingId } from '../../../shared/enums/footerLinkTrackingIds';
import { getContractRevocationUrl } from '../../helper/getContractRevocationUrl';
import { trackTextlinkClick } from '../../helper/trackingFooter';
import { type FooterContext } from '../../types';
import styles from './ContractCancellationButton.module.scss';

const messages = defineMessages({
    label: { defaultMessage: 'Vertrag widerrufen' },
    a11yLabel: { defaultMessage: 'Vertrag widerrufen, öffnet in neuem Tab' },
});

type Props = {
    readonly layout: 'checkout' | 'default';
};

const ContractCancellationButton = ({ layout }: Props): JSX.Element => {
    const intl = useIntl();
    const { tenantConfig } = useFragmentContext<FooterContext>();
    const url = getContractRevocationUrl(tenantConfig.urls.faq);
    const label = intl.formatMessage(messages.label);
    const trackingId = FooterLinkTrackingId.RevokeContract;

    const handleClick = (): void => {
        trackTextlinkClick(trackingId, label, url);
    };

    return (
        <div
            className={
                layout === 'checkout'
                    ? classNames('mt-2x', 'w-100', styles.wrapperCheckout)
                    : classNames('flex-1', 'd-flex', 'w-100', 'justify-content-center', 'mb-2x')
            }
            data-testid="contract-revocation-button"
        >
            <Button
                a11yText={intl.formatMessage(messages.a11yLabel)}
                as={RenderType.Link}
                color={ButtonColor.Brand}
                href={url}
                role="link"
                shape={ButtonShape.Rounded}
                size={ButtonSize.Small}
                type={ButtonType.Button}
                variant={ButtonVariant.Outlined}
                onClick={handleClick}
                target="_blank"
            >
                {label}
            </Button>
        </div>
    );
};

export { ContractCancellationButton };
